import { EventKey, trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import type { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import type { BetOptions } from '@core/Areas/FollowMe/Components/ProfileDisplay/types'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import type { FollowMeShareTicket } from '../types'
import { getRaceCodeFromTicket } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/analytics'
import { trackShareBetToBetLoopError } from '@core/Areas/ShareBet/helpers/analytics'
import { AppRoutes } from '@core/AppRoutes'

export const trackBetLoopSortFeed = (sortItem: string): void =>
  _trackBetLoopEvent({ interaction_type: 'Sort Feed', sortItem, location: getBetLoopLocation() })

export const trackBetLoopViewProfile = (): void =>
  _trackBetLoopEvent({
    interaction_type: 'View Profile',
    location: getBetLoopLocation(),
    activeTab: 'Bets',
    activePill: 'Pending Shares',
  })

export const trackBetLoopToggleFollow = (activeState: 'enable' | 'disable'): void =>
  _trackBetLoopEvent({
    interaction_type: 'Follow User',
    location: getBetLoopLocation(),
    activeState,
  })

export const trackBetLoopCopyBet = ({
  isFollowing,
  betType,
  ticket,
  share_origin,
}: {
  isFollowing: boolean
  betType: string
  ticket: FollowMeShareTicket
  share_origin: string
}): void =>
  _trackBetLoopEvent({
    interaction_type: 'Copy Bet',
    followState: isFollowing ? 'Following' : 'Not Following',
    betType,
    raceCode: getRaceCodeFromTicket(ticket),
    location: getBetLoopLocation(),
    share_origin,
  })

export const trackBetLoopCopyBetError = (
  message: string,
  isFollowing: boolean,
  betType: string
): void =>
  _trackBetLoopEvent({
    interaction_type: 'Copy Bet Error',
    followState: isFollowing ? 'Following' : 'Not Following',
    message,
    betType,
    location: getBetLoopLocation(),
  })

export const trackBetLoopNotificationToggle = (activeState: 'enable' | 'disable'): void =>
  _trackBetLoopEvent({
    interaction_type: 'Set Notification',
    location: getBetLoopLocation(),
    activeState,
  })

export const trackBetLoopNotificationOpened = (betType: string): void =>
  _trackBetLoopEvent({
    interaction_type: 'Notification Opened',
    betType,
  })

export const trackBetLoopViewMyProfile = (): void =>
  _trackBetLoopEvent({
    interaction_type: 'View My Profile',
    location: getBetLoopLocation(),
    activeTab: 'Bets',
    activePill: 'Pending Shares',
  })

export const trackBetLoopExpandSelection = (activeState: 'enable' | 'disable'): void =>
  _trackBetLoopEvent({
    interaction_type: 'Expand Selection',
    location: getBetLoopLocation(),
    activeState,
  })

export const trackBetLoopProfileTabAndPillChange = (
  interaction_type: 'Tab Change' | 'Pill Change',
  activeTab: 'Bets' | 'Stats',
  activePill?: BetOptions
): void =>
  _trackBetLoopEvent({
    interaction_type,
    location: getBetLoopLocation(),
    activeTab,
    activePill,
  })

export const trackBetLoopNavigateToRace = (ticket: FollowMeShareTicket): void =>
  _trackBetLoopEvent({
    interaction_type: 'Navigate to Race',
    location: getBetLoopLocation(),
    raceCode: getRaceCodeFromTicket(ticket),
  })

export const trackBetLoopShareBetBlocked = (data: ShareBetData, isEntitled: boolean): void => {
  let ticketType: string | undefined
  let raceCode: ShortRaceCode | undefined

  if (data.contextType === 'ticket') {
    ticketType = data.share.ticket.productName
    raceCode = getRaceCodeFromTicket(data.share.ticket)
  }

  if (data.contextType === 'betslip' || data.contextType === 'quickbet') {
    ticketType = data.share.items[0].bettingType
  }

  trackShareBetToBetLoopError({
    location: getBetLoopLocation(),
    betType: ticketType,
    destination: 'Bet Loop',
    message: !isEntitled ? 'Access Blocked' : 'Bet Not Eligible',
    raceCode,
  })
}

export const trackBetLoopShareProfile = (isMyProfile: boolean) => {
  _trackBetLoopEvent({
    interaction_type: isMyProfile ? 'Share My Profile' : 'Share User Profile',
  })
}

export const trackBetLoopEditMyProfile = (location: 'Onboarding' | 'My Profile') =>
  _trackBetLoopEvent({
    interaction_type: 'Edit My Profile',
    location: location,
  })

export const trackBetLoopLaunchBetLoop = () => {
  trackEvent(keys.navigationClick, {
    interaction_type: 'Navigate to Bet Loop',
    location: 'Onboarding',
  })
}

export const trackBetLoopCopyBetPlaced = (data: {
  amountPaid: number
  hasSpecialOffer: boolean
  legs: number
  raceCode: string | undefined
  origin: string
}) => trackEvent(keys.betLoop, { interaction_type: 'Bet Placed', ...data })

export const trackBetLoopEditAvatar = createTrackFnWithInteractionType('Edit Avatar')
export const trackBetLoopProfileUpdated = createTrackFnWithInteractionType('Profile Updated')
export const trackBetLoopRandomiseAvatar = createTrackFnWithInteractionType('Randomise Avatar')
export const trackBetLoopAvatarUpdated = createTrackFnWithInteractionType('Avatar Updated')
export const trackBetLoopCloseOnboarding = createTrackFnWithInteractionType('Close Onboarding')
export const trackBetLoopRestrictedAccess = createTrackFnWithInteractionType('Access Blocked')
export const trackBetLoopOnboardingTCDelay =
  createTrackFnWithInteractionType("Onboarding T&C's Delay")

// =============
// Local Helpers
// =============

function createTrackFnWithInteractionType(interaction_type: string) {
  return () => _trackBetLoopEvent({ interaction_type })
}

function _trackBetLoopEvent({
  event = keys.betLoop,
  interaction_type,
  sortItem,
  location,
  activeState,
  message,
  activeTab,
  activePill,
  followState,
  betType,
  raceCode,
  destination,
  share_origin,
}: {
  event?: EventKey
  interaction_type?: string
  sortItem?: string
  location?: BetLoopLocation
  activeState?: 'enable' | 'disable'
  message?: string
  activeTab?: 'Bets' | 'Stats'
  activePill?: 'Pending Shares' | 'Resulted Shares' | 'My Copies'
  followState?: 'Following' | 'Not Following'
  betType?: string
  raceCode?: ShortRaceCode
  destination?: string
  share_origin?: string
}) {
  trackEvent(event, {
    interaction_type,
    location,
    activeState,
    message,
    sortItem,
    activeTab,
    activePill,
    followState,
    betType: titleCaseSentence(betType),
    raceCode,
    destination,
    share_origin,
  })
}

function getBetLoopLocation(): BetLoopLocation {
  const url = new URL(window.location.href)

  const tab = url.searchParams.get('tab')
  const pathname = url.pathname

  if (pathname === AppRoutes.BetLoop) {
    if (tab === 'explore' || !tab) return 'Explore'
    if (tab === 'following') return 'Following'
  }

  if (pathname.includes(AppRoutes.BetLoopMyProfile)) {
    if (tab === 'followers') {
      return 'My Profile - Followers'
    } else if (tab === 'following') {
      return 'My Profile - Following'
    } else {
      return 'My Profile'
    }
  }

  if (pathname.includes(AppRoutes.BetLoopProfile)) {
    if (tab === 'followers') {
      return 'User Profile - Followers'
    } else if (tab === 'following') {
      return 'User Profile - Following'
    } else {
      return 'User Profile'
    }
  }

  return undefined
}

const titleCaseSentence = (sentence: string | undefined): string | undefined => {
  if (!sentence) return
  return sentence
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// =====
// Types
// =====

type ShortRaceCode = RaceCodesLowerCase | 'combination'

type BetLoopLocation =
  | 'Onboarding'
  | 'Explore'
  | 'Following'
  | 'My Profile'
  | 'My Profile - Followers'
  | 'My Profile - Following'
  | 'User Profile'
  | 'User Profile - Followers'
  | 'User Profile - Following'
  | undefined
